import { useEffect, useRef } from 'react';
import { hexToRgb, themeHex, useThemeVersion } from '@/modules/theme';
import { METER_CORNER_FRACTION } from '@/components/ui/meter-scale';

type Rgb = [number, number, number];

// The empty part: opaque at the centre line, fading to the rim, so the glass shows through
const emptyAlpha = (t: number) => {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const base = 1 - Math.pow(1 - t, 1.4);
  if (t < 0.08) {
    const u = t / 0.08;
    return base * u * u * (3 - 2 * u);
  }
  return base;
};

// The filled part keeps its body and softens only its outer fifth
const filledAlpha = (t: number) => {
  if (t <= 0) return 0;
  if (t >= 0.2) return 1;
  const u = t / 0.2;
  return u * u * u * (u * (u * 6 - 15) + 10);
};

function mix(stops: Array<[number, Rgb]>, t: number): Rgb {
  if (t <= stops[0][0]) return stops[0][1];
  for (let i = 0; i < stops.length - 1; i++) {
    const [t1, c1] = stops[i];
    const [t2, c2] = stops[i + 1];
    if (t <= t2) {
      const k = (t - t1) / (t2 - t1);
      return [c1[0] + (c2[0] - c1[0]) * k, c1[1] + (c2[1] - c1[1]) * k, c1[2] + (c2[2] - c1[2]) * k];
    }
  }
  return stops[stops.length - 1][1];
}

function coverage(x: number, y: number, w: number, h: number, aa: number) {
  const r = h / 2;
  const dist = Math.hypot(x - Math.max(r, Math.min(w - r, x)), y - r);
  if (dist <= r - aa / 2) return 1;
  if (dist >= r + aa / 2) return 0;
  return 1 - (dist - (r - aa / 2)) / aa;
}

/**
 * The editor's story size meter, as production drew it: a glowing tube, mint into coral into aubergine across its length,
 * soft at its rim, filled up to the value and a desaturated haze beyond it (in a panel, `saturated`, the haze keeps the
 * ramp's colours). Painted pixel by pixel from the theme's colours.
 */
export function MeterCanvas({ width, height, value, saturated = false }: { width: number; height: number; value: number; saturated?: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const themeVersion = useThemeVersion();

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    const W = canvas.width;
    const H = canvas.height;
    const stops: Array<[number, Rgb]> = [
      [0, hexToRgb(themeHex('lureo-mint'))],
      [0.5, hexToRgb(themeHex('lureo-coral'))],
      [1, hexToRgb(themeHex('lureo-emphasis-strong'))],
    ];
    const empty = hexToRgb(themeHex('lureo-meter-empty'));
    const image = ctx.createImageData(W, H);
    const d = image.data;
    const half = H / 2;
    const corner = half * METER_CORNER_FRACTION;
    const edge = value <= 0 ? 0 : corner + Math.min(value, 1) * (W - 2 * corner);
    const soft = 4 * dpr;
    const aa = 1.5 * dpr;
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        const cover = coverage(x, y, W, H, aa);
        if (cover <= 0) continue;
        const inside = Math.max(0, 1 - Math.hypot(x - Math.max(half, Math.min(W - half, x)), y - half) / half);
        const filled = x <= edge - soft ? 1 : x >= edge ? 0 : (edge - x) / soft;
        const [r, g, b] = mix(stops, x / W);
        const [er, eg, eb] = saturated ? [r, g, b] : empty;
        const i = (y * W + x) * 4;
        d[i] = Math.round(er + (r - er) * filled);
        d[i + 1] = Math.round(eg + (g - eg) * filled);
        d[i + 2] = Math.round(eb + (b - eb) * filled);
        d[i + 3] = Math.round((emptyAlpha(inside) * 0.6 * (1 - filled) + filledAlpha(inside) * filled) * cover * 255);
      }
    }
    ctx.putImageData(image, 0, 0);
  }, [width, height, value, saturated, themeVersion]);

  return <canvas ref={ref} aria-hidden className="block h-full w-full" />;
}
